import Phaser from 'phaser';
import type { CritterTypeConfig } from '@/config/critters.config';

/**
 * AmbientCritter — pojedyncze dekoracyjne stworzonko tła (motyl, ptak, biedronka,
 * tumbleweed…). Bez roli w gameplayu: spawnowane przez `AmbientCrittersSystem`,
 * porusza się po ścieżce zależnej od `behavior`, a po wyjściu z kadru / upływie
 * życia zgłasza `done` (manager je niszczy).
 *
 * Animację RUCHU robi kod (ścieżka + sin-bob + opcjonalna rotacja), więc jedno
 * stworzonko = jeden statyczny transparent sticker PNG (jak pet). Pozycja świata
 * (`scrollFactor=1`) — stworzonko żyje w świecie, gracz może je minąć.
 *
 * Wzorce (`behavior`):
 *  - `flutter` — meandruje po lokalnym obszarze (cel→cel jak StallPet) + sin-bob,
 *    po `lifetimeMs` fade-out → done. (motyl, biedronka)
 *  - `cross`   — wlatuje z jednej krawędzi viewportu, leci prosto na drugą
 *    (+sin-bob, +rotacja jeśli `rotateSpeed`, flipX wg kierunku), done za
 *    krawędzią. (ptak, tumbleweed, zając)
 *  - `soar`    — krąży po elipsie `loops` razy nad kadrem → done. (drapieżnik)
 *
 * Ścieżka liczona przy spawnie z `view` (cam.worldView) przekazanego w opts.
 */
type Mode = 'wander' | 'pause';

export interface AmbientCritterOpts {
  cfg: CritterTypeConfig;
  /** Aktualny `cam.worldView` przy spawnie — bazka do liczenia ścieżki. */
  view: Phaser.Geom.Rectangle;
}

export class AmbientCritter {
  readonly sprite: Phaser.GameObjects.Image;

  private readonly cfg: CritterTypeConfig;
  private readonly speed: number;
  private readonly halfSize: number;

  // Pozycja logiczna (bez bob/rotacji — te idą jako offset renderu).
  private x = 0;
  private y = 0;
  private readonly bobPhase = Math.random() * Math.PI * 2;
  private elapsedMs = 0;
  private fading = false;

  // cross
  private dirX = 1;
  private exitX = 0;
  private baseY = 0;

  // flutter
  private mode: Mode = 'wander';
  private targetX = 0;
  private targetY = 0;
  private pauseUntil = 0;
  private readonly roam: Phaser.Geom.Rectangle;
  private lifetimeMs: number;
  /** `true` gdy aktualnie ucieka przed graczem (behavior flutter + cfg.flee). */
  private fleeing = false;

  // soar
  private centerX = 0;
  private centerY = 0;
  private radiusX = 0;
  private radiusY = 0;
  private angle = 0;
  private angleTravelled = 0;
  private angularSpeed = 0;
  private loopsTotal = 0;

  constructor(scene: Phaser.Scene, opts: AmbientCritterOpts) {
    const cfg = opts.cfg;
    const view = opts.view;
    this.cfg = cfg;
    this.speed = rand(cfg.speed.min, cfg.speed.max);

    const tex = scene.textures.exists(cfg.textureKey) ? cfg.textureKey : 'placeholder-square';
    this.sprite = scene.add.image(0, 0, tex).setDepth(cfg.depth);

    const targetPx = rand(cfg.scale.min, cfg.scale.max);
    const baseScale = targetPx / Math.max(this.sprite.width, this.sprite.height);
    this.sprite.setScale(baseScale);
    this.halfSize = targetPx / 2;

    // Y w paśmie `bandY` (0=góra,1=dół viewportu). Domyślnie cały kadr.
    const band = cfg.bandY ?? { min: 0.1, max: 0.9 };
    const bandY = view.y + view.height * rand(band.min, band.max);

    this.lifetimeMs = jitter(cfg.lifetimeMs ?? 6000, 0.25);
    this.roam = new Phaser.Geom.Rectangle(
      view.x + view.width * 0.1,
      Math.max(view.y, bandY - view.height * 0.12),
      view.width * 0.8,
      view.height * 0.24,
    );

    switch (cfg.behavior) {
      case 'cross': {
        this.dirX = Math.random() < 0.5 ? 1 : -1;
        this.baseY = bandY;
        const m = this.halfSize + 8;
        this.x = this.dirX > 0 ? view.x - m : view.right + m;
        this.exitX = this.dirX > 0 ? view.right + m : view.x - m;
        this.y = this.baseY;
        if (cfg.flipToFaceDir) this.sprite.setFlipX(this.dirX < 0);
        break;
      }
      case 'soar': {
        this.centerX = view.centerX;
        this.centerY = bandY;
        this.radiusX = view.width * 0.32;
        this.radiusY = view.height * 0.14;
        this.angle = Math.random() * Math.PI * 2;
        this.angularSpeed = this.speed / Math.max(40, this.radiusX); // rad/s
        this.loopsTotal = cfg.loops ?? 2;
        this.x = this.centerX + Math.cos(this.angle) * this.radiusX;
        this.y = this.centerY + Math.sin(this.angle) * this.radiusY;
        break;
      }
      case 'flutter':
      default: {
        this.x = rand(this.roam.left, this.roam.right);
        this.y = rand(this.roam.top, this.roam.bottom);
        this.pickFlutterTarget();
        break;
      }
    }

    this.sprite.setPosition(this.x, this.y);
  }

  /**
   * Tick. `dt` w sekundach, `now` w ms. `playerX/Y` (world coords) opcjonalne —
   * używane przez `flutter` + `cfg.flee` do ucieczki przed graczem.
   * Zwraca `true` gdy stworzonko skończyło.
   */
  update(dt: number, now: number, playerX?: number, playerY?: number): boolean {
    this.elapsedMs += dt * 1000;
    let done = false;

    switch (this.cfg.behavior) {
      case 'cross':
        done = this.updateCross(dt);
        break;
      case 'soar':
        done = this.updateSoar(dt);
        break;
      case 'flutter':
      default:
        done = this.updateFlutter(dt, now, playerX, playerY);
        break;
    }

    // Render = pozycja logiczna + sin-bob (jeśli skonfigurowany).
    let renderY = this.y;
    if (this.cfg.bob) {
      const t = (this.elapsedMs / 1000) * this.cfg.bob.freqHz * Math.PI * 2;
      renderY += Math.sin(t + this.bobPhase) * this.cfg.bob.amplitudePx;
    }
    this.sprite.setPosition(this.x, renderY);

    if (this.cfg.rotateSpeed) {
      this.sprite.rotation += Phaser.Math.DegToRad(this.cfg.rotateSpeed) * dt;
    }

    return done;
  }

  destroy(): void {
    this.sprite.destroy();
  }

  // ── Behaviors ──────────────────────────────────────────────────────────────

  private updateCross(dt: number): boolean {
    this.x += this.dirX * this.speed * dt;
    return this.dirX > 0 ? this.x >= this.exitX : this.x <= this.exitX;
  }

  private updateSoar(dt: number): boolean {
    // Liczymy PRZEBYTY kąt osobno od `angle` (które startuje losowo dla pozycji),
    // żeby `loops` zawsze oznaczał pełną liczbę okrążeń niezależnie od startu.
    const step = this.angularSpeed * dt;
    this.angle += step;
    this.angleTravelled += step;
    this.x = this.centerX + Math.cos(this.angle) * this.radiusX;
    this.y = this.centerY + Math.sin(this.angle) * this.radiusY;
    return this.angleTravelled >= this.loopsTotal * Math.PI * 2;
  }

  private updateFlutter(dt: number, now: number, playerX?: number, playerY?: number): boolean {
    // ── Reakcja na gracza: ucieczka + powrót (cfg.flee) ──────────────────────
    const flee = this.cfg.flee;
    if (flee && playerX !== undefined && playerY !== undefined) {
      const fdx = this.x - playerX;
      const fdy = this.y - playerY;
      const pdist = Math.hypot(fdx, fdy);
      if (!this.fleeing && pdist < flee.radiusPx) {
        this.fleeing = true;
      } else if (this.fleeing && pdist > flee.radiusPx * 1.35) {
        // Gracz się oddalił — przestań uciekać.
        this.fleeing = false;
        if (flee.curious) {
          // „Zaciekawiony" — podpełznij z powrotem w stronę gracza.
          this.targetX = clamp(playerX + (Math.random() * 2 - 1) * 40, this.roam.left, this.roam.right);
          this.targetY = clamp(playerY + (Math.random() * 2 - 1) * 40, this.roam.top, this.roam.bottom);
          this.mode = 'wander';
        } else {
          this.mode = 'pause';
          this.pauseUntil = now + Phaser.Math.Between(200, 600);
        }
      }
      if (this.fleeing) {
        // Ucieka prosto od gracza, szybciej niż meandruje.
        const d = pdist || 1;
        const step = this.speed * flee.speedMult * dt;
        this.x += (fdx / d) * step;
        this.y += (fdy / d) * step;
        // Spłoszone nie więdnie — odepchnij lifetime, żeby po pościgu jeszcze
        // chwilę pożyło (i nie znikło tuż po tym jak gracz odejdzie).
        if (this.elapsedMs + 2500 > this.lifetimeMs) this.lifetimeMs = this.elapsedMs + 2500;
        return false;
      }
    }

    if (this.mode === 'pause') {
      if (now >= this.pauseUntil) this.pickFlutterTarget();
    } else {
      const dx = this.targetX - this.x;
      const dy = this.targetY - this.y;
      const dist = Math.hypot(dx, dy);
      const step = this.speed * dt;
      if (dist <= step) {
        this.x = this.targetX;
        this.y = this.targetY;
        this.mode = 'pause';
        this.pauseUntil = now + Phaser.Math.Between(300, 1100);
      } else {
        this.x += (dx / dist) * step;
        this.y += (dy / dist) * step;
      }
    }

    // Życie: po lifetimeMs zaczynamy fade-out; gdy niewidoczny → done.
    if (!this.fading && this.elapsedMs >= this.lifetimeMs) this.fading = true;
    if (this.fading) {
      const a = this.sprite.alpha - dt / 0.6; // ~0.6s fade
      this.sprite.setAlpha(Math.max(0, a));
      if (this.sprite.alpha <= 0.01) return true;
    }
    return false;
  }

  private pickFlutterTarget(): void {
    this.targetX = rand(this.roam.left, this.roam.right);
    this.targetY = rand(this.roam.top, this.roam.bottom);
    this.mode = 'wander';
  }
}

// ─── Helpers ────────────────────────────────────────────────────────────────

function rand(min: number, max: number): number {
  return min + Math.random() * (max - min);
}

function clamp(v: number, min: number, max: number): number {
  return v < min ? min : v > max ? max : v;
}

/** Zwraca `base` ± `frac` (np. jitter(6000, 0.25) → 4500..7500). */
function jitter(base: number, frac: number): number {
  return base * (1 + (Math.random() * 2 - 1) * frac);
}
